// /src/services/tokenStorage.ts
import { getJwtExp } from '@/utils/jwtUtils'

interface TokenData {
  accessToken: string
  /** Rỗng khi backend SSO không trả refresh (chỉ dùng access JWT) */
  refreshToken: string
  expiresAt?: number
  userType?: 'user' | 'customer'
}

class TokenStorage {
  private readonly ACCESS_TOKEN_KEY = 'accessToken'
  private readonly REFRESH_TOKEN_KEY = 'refreshToken'
  private readonly TOKEN_DATA_KEY = 'tokenData'
  private readonly LANGUAGE_KEY = 'RaStore.locale'

  /**
   * Store access token; refresh optional (một số API SSO chỉ trả access_token).
   */
  setTokens(accessToken: string, refreshToken?: string | null, userType?: 'user' | 'customer'): void {
    try {
      const refresh = refreshToken != null && String(refreshToken).trim() !== '' ? String(refreshToken) : ''
      const expiresAt = getJwtExp(accessToken)
      if (expiresAt == null) {
        console.warn('Could not read exp from access JWT')
      }

      const tokenData: TokenData = {
        accessToken: accessToken,
        refreshToken: refresh,
        expiresAt: expiresAt,
        userType: userType,
      }

      // Store in multiple formats for backward compatibility
      localStorage.setItem(this.ACCESS_TOKEN_KEY, accessToken)
      localStorage.setItem(this.REFRESH_TOKEN_KEY, refresh)
      localStorage.setItem(this.TOKEN_DATA_KEY, JSON.stringify(tokenData))

      if (userType) {
        localStorage.setItem('userType', userType)
      }
    } catch (error) {
      console.error('Error storing tokens:', error)
    }
  }

  /**
   * Get access token
   */
  getAccessToken(): string | null {
    try {
      // Try new format first
      const tokenDataStr = localStorage.getItem(this.TOKEN_DATA_KEY)
      if (tokenDataStr) {
        const tokenData: TokenData = JSON.parse(tokenDataStr)
        return tokenData.accessToken
      }

      // Fallback to old format
      return localStorage.getItem(this.ACCESS_TOKEN_KEY)
    } catch (error) {
      console.error('Error getting access token:', error)
      return localStorage.getItem(this.ACCESS_TOKEN_KEY)
    }
  }

  /**
   * Get refresh token
   */
  getRefreshToken(): string | null {
    try {
      // Try new format first
      const tokenDataStr = localStorage.getItem(this.TOKEN_DATA_KEY)
      if (tokenDataStr) {
        const tokenData: TokenData = JSON.parse(tokenDataStr)
        return tokenData.refreshToken
      }

      // Fallback to old format
      return localStorage.getItem(this.REFRESH_TOKEN_KEY)
    } catch (error) {
      console.error('Error getting refresh token:', error)
      return localStorage.getItem(this.REFRESH_TOKEN_KEY)
    }
  }

  /**
   * Get all token data
   */
  getTokenData(): TokenData | null {
    try {
      const tokenDataStr = localStorage.getItem(this.TOKEN_DATA_KEY)
      if (tokenDataStr) {
        return JSON.parse(tokenDataStr)
      }

      // Fallback: construct from old format
      const accessToken = localStorage.getItem(this.ACCESS_TOKEN_KEY)
      const refreshToken = localStorage.getItem(this.REFRESH_TOKEN_KEY)

      if (accessToken) {
        return {
          accessToken: accessToken,
          refreshToken: refreshToken ?? '',
        }
      }

      return null
    } catch (error) {
      console.error('Error getting token data:', error)
      return null
    }
  }

  /**
   * Get language
   */
  getLanguage(): string | null {
    try {
      const language = localStorage.getItem(this.LANGUAGE_KEY) || 'vi'
      return language.replace(/^["']|["']$/g, '')
    } catch (error) {
      console.error('Error getting language:', error)
      return 'vi'
    }
  }

  /**
   * Check if access token is expired
   */
  isAccessTokenExpired(): boolean {
    try {
      const tokenData = this.getTokenData()
      if (!tokenData?.expiresAt) {
        const accessToken = this.getAccessToken()
        if (!accessToken) return true

        const exp = getJwtExp(accessToken)
        if (exp == null) return false
        const currentTime = Math.floor(Date.now() / 1000)
        return exp <= currentTime
      }

      const currentTime = Math.floor(Date.now() / 1000)
      return tokenData.expiresAt <= currentTime
    } catch (error) {
      console.error('Error checking token expiration:', error)
      return true // Assume expired if we can't check
    }
  }

  /**
   * Check if access token will expire soon (within 5 minutes)
   */
  willAccessTokenExpireSoon(): boolean {
    try {
      const tokenData = this.getTokenData()
      if (!tokenData?.expiresAt) {
        const accessToken = this.getAccessToken()
        if (!accessToken) return true

        const exp = getJwtExp(accessToken)
        if (exp == null) return false
        const currentTime = Math.floor(Date.now() / 1000)
        return exp - currentTime < 300
      }

      const currentTime = Math.floor(Date.now() / 1000)
      const timeUntilExpiry = tokenData.expiresAt - currentTime

      return timeUntilExpiry < 300 // 5 minutes
    } catch (error) {
      console.error('Error checking token expiration:', error)
      return true
    }
  }

  /**
   * Get user type from stored tokens
   */
  getUserType(): 'user' | 'customer' | null {
    try {
      // Try new format first
      const tokenData = this.getTokenData()
      if (tokenData?.userType) {
        return tokenData.userType
      }

      // Fallback to separate storage
      return localStorage.getItem('userType') as 'user' | 'customer' | null
    } catch (error) {
      console.error('Error getting user type:', error)
      return null
    }
  }

  /**
   * Clear all tokens
   */
  clearTokens(): void {
    try {
      localStorage.removeItem(this.ACCESS_TOKEN_KEY)
      localStorage.removeItem(this.REFRESH_TOKEN_KEY)
      localStorage.removeItem(this.TOKEN_DATA_KEY)
      localStorage.removeItem('userType')
    } catch (error) {
      console.error('Error clearing tokens:', error)
    }
  }

  /**
   * Check if user is authenticated
   */
  /** Có access JWT còn hạn (không phụ thuộc refresh). */
  isAuthenticated(): boolean {
    const accessToken = this.getAccessToken()
    return !!accessToken && !this.isAccessTokenExpired()
  }

  /**
   * Check if we have valid tokens (access or refresh)
   */
  hasValidTokens(): boolean {
    const accessToken = this.getAccessToken()
    if (!accessToken) return false
    if (!this.isAccessTokenExpired()) return true
    const refreshToken = this.getRefreshToken()
    return !!(refreshToken && refreshToken.trim())
  }
}

class ProfileStorage {
  private readonly PROFILE_KEY = 'userProfile'

  /**
   * Store user profile
   */
  setProfile(profile: any): void {
    try {
      localStorage.setItem(this.PROFILE_KEY, JSON.stringify(profile))
    } catch (error) {
      console.error('Error storing profile:', error)
    }
  }

  /**
   * Get user profile
   */
  getProfile(): any {
    try {
      const profileStr = localStorage.getItem(this.PROFILE_KEY)
      if (profileStr) {
        return JSON.parse(profileStr)
      }
      return null
    } catch (error) {
      console.error('Error getting profile:', error)
      return null
    }
  }

  /**
   * Clear user profile
   */
  clearProfile(): void {
    try {
      localStorage.removeItem(this.PROFILE_KEY)
    } catch (error) {
      console.error('Error clearing profile:', error)
    }
  }

  /**
   * Update specific profile fields
   */
  updateProfile(updates: Partial<any>): void {
    try {
      const currentProfile = this.getProfile()
      if (currentProfile) {
        const updatedProfile = { ...currentProfile, ...updates }
        this.setProfile(updatedProfile)
      }
    } catch (error) {
      console.error('Error updating profile:', error)
    }
  }

  /**
   * Check if profile exists
   */
  hasProfile(): boolean {
    return !!this.getProfile()
  }
}

// Export singleton instances
export const tokenStorage = new TokenStorage()
export const profileStorage = new ProfileStorage()

// Export classes for testing
export { TokenStorage, ProfileStorage }
